import { getMemberHash } from '@vista-digital/occ-domain'
import { getStoredMemberHash } from '../../utils'

export class Gtm {
    public startInTicks: number
    public uniqueEventId?: number
    public element?: string

    public constructor(uniqueEventId?: number, element?: string) {
        this.startInTicks = new Date().getTime()
        this.uniqueEventId = uniqueEventId
        this.element = element
    }
}

export class DataLayer {
    public event: string
    public gtm: Gtm
    public customerId: string | null | undefined

    public constructor(event: string, uniqueEventId?: number, element?: string) {
        this.event = event
        this.gtm = new Gtm(uniqueEventId, element)
        this.customerId = getStoredMemberHash() || getMemberHash()
    }
}

// ---- Ecommerce ----

export interface EcommerceItemListProps {
    item_list_id?: string
    item_list_name?: string
}

export class EcommerceItem implements EcommerceItemListProps {
    public constructor(model?: EcommerceItem) {
        if (model) Object.assign(this, model)
    }

    public item_id: string
    public item_name: string

    public item_category: string
    public item_category2?: string = 'na'
    public item_category3?: string = 'na'
    public item_category4?: string = 'na'
    public item_category5?: string = 'na'

    public item_variant?: string

    // List properties
    public index?: number | string
    public item_list_id?: string
    public item_list_name?: string

    public user_path?: string
}

export class EcommercePricedItem extends EcommerceItem {
    public constructor(model?: EcommercePricedItem) {
        super(model)
        if (model) {
            this.price = Math.round((model?.price ?? 0) * 100) / 100
            this.quantity = model.quantity
        }
    }

    public price?: number
    public quantity?: number
}

export interface EcommerceShowtimeProps {
    performance_date: string
    performance_time: string
    showtime_id: string
    screen_type: string
    cinema_name: string
}

export class EcommerceShowtimeItem extends EcommercePricedItem implements EcommerceShowtimeProps {
    public constructor(model?: EcommerceShowtimeItem) {
        super(model)
        if (model) {
            this.performance_date = model.performance_date
            this.performance_time = model.performance_time
            this.showtime_id = model.showtime_id
            this.screen_type = model.screen_type
            this.cinema_name = model.cinema_name
        }
    }

    public performance_date: string
    public performance_time: string
    public showtime_id: string
    public screen_type: string
    public cinema_name: string
}

export interface EcommerceFnbProps {
    cinema_name: string
}

export class EcommerceFnbItem extends EcommercePricedItem implements EcommerceFnbProps {
    public constructor(model?: EcommerceFnbItem) {
        super(model)
        if (model) {
            this.cinema_name = model.cinema_name
        }
    }

    public cinema_name: string
}

export class Ecommerce {
    public constructor(
        items: EcommerceItem[],
        value?: number,
        transactionId?: string,
        vistaTransactionId?: number,
        isoCurrencyCode?: string
    ) {
        this.items = items

        this.currency = isoCurrencyCode
        if (value) this.value = value
        if (transactionId) this.transaction_id = transactionId
        if (vistaTransactionId) this.vista_transaction_id = vistaTransactionId
    }

    public currency?: string
    public value?: number
    public transaction_id?: string
    public vista_transaction_id?: number

    public items: EcommerceItem[] | EcommercePricedItem[] | EcommerceFnbItem[]
}

export interface EcommercePromotionItem {
    creative_name: string
    creative_slot: string
    promotion_id: string
    promotion_name: string
}

export class EcommercePromotion {
    public constructor(items: EcommercePromotionItem[]) {
        this.items = items
    }

    public items: EcommercePromotionItem[]
}

export class EcommerceDataLayer extends DataLayer {
    public ecommerce: Ecommerce | EcommercePromotion | EcommercePricedItem | EcommerceFnbItem

    public constructor(
        event: string,
        ecommerce: Ecommerce | EcommercePromotion | EcommercePricedItem | EcommerceFnbItem,
        uniqueEventId?: number,
        element?: string
    ) {
        super(event, uniqueEventId, element)
        this.ecommerce = ecommerce
    }
}
